import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ColorPickerModule } from '@iplab/ngx-color-picker';
import { TextEditorComponent, TextEditorStyle } from '../../../../../shared/components/text-editor';

export interface FleetsPanelData {
  title?: string;
  subtitle?: string;
  titleStyle?: TextEditorStyle;
  formTheme?: 'light' | 'dark';
  buttonText?: string;
  backgroundColor?: string;
}

/**
 * Panel component for configuring a "Fleets" section in the website builder — provides controls to edit title, subtitle, title style, theme, button text and background color (including a custom color picker) with live preview and data binding.
 * @component
 * @selector app-fleets-panel
 * @example
 *   <app-fleets-panel [data]="panelData" (close)="handleClose()" (save)="handleSave($event)" (dataChange)="handleDataChange($event)"></app-fleets-panel>
 * @input {FleetsPanelData} data - Configuration object for the panel. Expected fields include title, subtitle, titleStyle (TextEditorStyle), formTheme ('light' | 'dark'), buttonText, backgroundColor. The component reads initial values from this input and emits updates via dataChange and save.
 * @output {void} close - Emitted when the panel close action is triggered (e.g. user cancels or closes the editor).
 * @output {FleetsPanelData} save - Emitted when the user saves the current panel configuration. Emits a shallow copy of the current data.
 * @output {FleetsPanelData} dataChange - Emitted whenever the internal data object is changed (e.g. title, subtitle, theme, button text, background color) to support two-way binding patterns.
 *
 * Key methods:
 * - onClose(): Emits the close event to notify parent that the panel should be closed.
 * - switchTab(tab: 'content' | 'layout'): Switches between the content and layout editing tabs.
 * - toggleSection(section: string): Toggles expansion of a given settings section; collapses other standard sections when opening one.
 * - isSectionExpanded(section: string): Returns whether a given section is currently expanded.
 * - onTitleChange(value: string) / onSubtitleChange(value: string): Update respective fields on the data object and emit dataChange.
 * - onTitleStyleChange(style: TextEditorStyle): Update title styling on the data object and emit dataChange.
 * - selectTheme(theme: 'light' | 'dark'): Update selectedTheme and data.formTheme and emit dataChange.
 * - onButtonTextChange(): Notify and emit dataChange after button text is edited.
 * - onBackgroundColorChange(color: string): Apply a new background color to data and emit dataChange.
 * - onAddColor(): Opens the color picker to add a custom color.
 * - openColorPicker(color?: string): Opens the color picker and initializes its preview color.
 * - closeColorPicker(): Closes the color picker, normalizes the picked color to a hex string, applies it immediately, and records it in the custom colors list (max 1 custom color).
 * - onColorPickerChange(): Handles live changes from the color picker for preview purposes without immediately adding to the custom color list.
 * - onSave(): Emits the save event with a copy of the current data.
 *
 * Lifecycle hooks:
 * - ngOnInit(): Initializes selected theme from the provided data and loads any custom background color on initial load.
 * - ngOnChanges(changes: SimpleChanges): Responds to incoming changes to the data input (re-syncs selectedTheme and background color, and loads custom colors when appropriate).
 *
 * Internal state and behavior notes:
 * - Maintains activeTab ('content' | 'layout'), expandedSections map for UI accordion behavior, selectedTheme, a single-entry customColors list, and color picker state (isColorPickerOpen, currentPickerColor).
 * - Uses a small predefined palette (PREDEFINED_COLORS) and only stores a custom color if the chosen background color is not in the predefined list (replacing any existing custom color).
 * - Emits shallow copies of data for save and dataChange to avoid unintended external mutation.
 */
@Component({
  selector: 'app-fleets-panel',
  standalone: true,
  imports: [CommonModule, FormsModule, ColorPickerModule, TextEditorComponent],
  templateUrl: './fleets-panel.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './fleets-panel.component.scss',
})
export class FleetsPanelComponent implements OnInit, OnChanges {
  @Input() data: FleetsPanelData = {
    title: '',
    subtitle: '',
    formTheme: 'light',
    buttonText: 'Submit',
    backgroundColor: '#FFFFFF',
  };
  @Output() close = new EventEmitter<void>();
  @Output() save = new EventEmitter<FleetsPanelData>();
  @Output() dataChange = new EventEmitter<FleetsPanelData>();

  activeTab: 'content' | 'layout' = 'content';
  expandedSections: Record<string, boolean> = {
    heading: true,
    'form-theme': false,
    button: false,
    background: false,
  };

  selectedTheme: 'light' | 'dark' = 'light';
  customColors: string[] = [];

  // Color picker state
  isColorPickerOpen = false;
  currentPickerColor: string | any = '#000000';

  private static readonly PREDEFINED_COLORS = ['#5E19A1', '#FF9736', '#079271', '#1B5BFF'];

  constructor() {}

  ngOnInit(): void {
    // Initialize theme from data
    if (this.data.formTheme) {
      this.selectedTheme = this.data.formTheme;
    }
    // Load custom color on initial load
    this.loadCustomColorFromData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      if (this.data?.formTheme) {
        this.selectedTheme = this.data.formTheme;
      }
      if (this.data?.backgroundColor) {
        // Load custom color if backgroundColor is not in predefined colors
        this.loadCustomColorFromData();
      }
    }
  }

  // Load custom color from data if it's not a predefined color
  /**
   * Loads the custom background color from the component's data and updates the component's customColors array. If this.data.backgroundColor is not present, the method does nothing. If the background color is not one of the predefined colors it is added as the single custom color; otherwise customColors is cleared.
   * @example
   * loadCustomColorFromData()
   * // undefined (updates this.customColors or leaves it unchanged)
   * @param {void} none - No arguments.
   * @returns {void} No return value.
   */
  private loadCustomColorFromData(): void {
    if (!this.data?.backgroundColor) {
      return;
    }

    const bgColor = this.data.backgroundColor;

    // Check if the color is not in predefined colors
    if (!FleetsPanelComponent.PREDEFINED_COLORS.includes(bgColor)) {
      // Add to custom colors (maximum 1 custom color)
      this.customColors = [bgColor];
    } else {
      // If it's a predefined color, clear custom colors
      this.customColors = [];
    }
  }

  onClose(): void {
    this.close.emit();
  }

  switchTab(tab: 'content' | 'layout'): void {
    this.activeTab = tab;
  }

  /**
   * Toggle the expanded/collapsed state of a named section in the UI; expanding certain standard sections (heading, form-theme, button, background) will first collapse other standard sections.
   * @example
   * oggleSection('heading')
   * undefined
   * @param {{string}} {{section}} - The key/name of the section to toggle (e.g. 'heading', 'form-theme', 'button', 'background', or others).
   * @returns {{void}} No return value.
   **/
  toggleSection(section: string): void {
    const wasExpanded = this.expandedSections[section] || false;

    if (wasExpanded) {
      // If clicking an expanded section, just collapse it
      this.expandedSections[section] = false;
    } else {
      // If clicking a collapsed section, close all others first, then expand this one
      // Only do this for standard sections (heading, form-theme, button, background)
      if (
        section === 'heading' ||
        section === 'form-theme' ||
        section === 'button' ||
        section === 'background'
      ) {
        Object.keys(this.expandedSections).forEach(key => {
          if (
            key === 'heading' ||
            key === 'form-theme' ||
            key === 'button' ||
            key === 'background'
          ) {
            this.expandedSections[key] = false;
          }
        });
      }
      this.expandedSections[section] = true;
    }
  }

  isSectionExpanded(section: string): boolean {
    return this.expandedSections[section] || false;
  }

  onTitleChange(value: string): void {
    this.data.title = value;
    this.emitDataChange();
  }

  onTitleStyleChange(style: TextEditorStyle): void {
    this.data.titleStyle = style;
    this.emitDataChange();
  }

  onSubtitleChange(value: string): void {
    this.data.subtitle = value;
    this.emitDataChange();
  }

  selectTheme(theme: 'light' | 'dark'): void {
    this.selectedTheme = theme;
    this.data.formTheme = theme;
    this.emitDataChange();
  }

  onButtonTextChange(): void {
    this.emitDataChange();
  }

  onBackgroundColorChange(color: string): void {
    this.data.backgroundColor = color;
    this.emitDataChange();
  }

  onAddColor(): void {
    // Open color picker modal
    this.openColorPicker();
  }

  // Open color picker for custom color
  openColorPicker(color?: string): void {
    this.currentPickerColor = color || this.data.backgroundColor || '#000000';
    this.isColorPickerOpen = true;
  }

  // Close color picker and add the selected color to custom colors list
  /**
   * Close the color picker, apply the selected color (normalizing to a hex string if necessary), add it to custom colors (replacing any existing custom), and mark the picker as closed.
   * @example
   * loseColorPicker()
   * // void
   * @returns {void} No return value; performs side effects: applies the background color, updates custom colors, and closes the picker.
   **/
  closeColorPicker(): void {
    if (this.currentPickerColor) {
      // Extract hex color from color object (chrome-picker returns an object with hex property)
      let hexColor: string;
      if (typeof this.currentPickerColor === 'string') {
        hexColor = this.currentPickerColor;
      } else if (
        this.currentPickerColor &&
        typeof this.currentPickerColor === 'object' &&
        'hex' in this.currentPickerColor
      ) {
        hexColor = (this.currentPickerColor as any).hex;
      } else {
        hexColor = String(this.currentPickerColor);
      }

      // Ensure hex color starts with #
      if (!hexColor.startsWith('#')) {
        hexColor = '#' + hexColor;
      }

      // Apply the color immediately
      this.onBackgroundColorChange(hexColor);

      // Add to custom colors (maximum 1 custom color - replace if exists)
      this.addCustomColor(hexColor);
    }
    this.isColorPickerOpen = false;
  }

  // Handle color change from color picker (update preview only, don't add to list yet)
  /**
   * Handle color picker changes by normalizing the selected color to a hex string, updating this.currentPickerColor, and applying the color as a preview via onBackgroundColorChange (does not add to custom colors list).
   * @example
   * nColorPickerChange()
   * undefined
   * @returns {void} No return value; updates component state and triggers a preview update.
   **/
  onColorPickerChange(): void {
    if (this.currentPickerColor) {
      // Extract hex color from color object (chrome-picker returns an object with hex property)
      let hexColor: string;
      if (typeof this.currentPickerColor === 'string') {
        hexColor = this.currentPickerColor;
      } else if (
        this.currentPickerColor &&
        typeof this.currentPickerColor === 'object' &&
        'hex' in this.currentPickerColor
      ) {
        hexColor = (this.currentPickerColor as any).hex;
      } else {
        hexColor = String(this.currentPickerColor);
      }

      // Ensure hex color starts with #
      if (!hexColor.startsWith('#')) {
        hexColor = '#' + hexColor;
      }

      // Update currentPickerColor to the hex string for consistency
      this.currentPickerColor = hexColor;

      // Apply the color immediately for preview (but don't add to custom colors list yet)
      this.onBackgroundColorChange(hexColor);
    }
  }

  private addCustomColor(color: string): void {
    // Maximum 1 custom color - replace existing if any
    if (!FleetsPanelComponent.PREDEFINED_COLORS.includes(color)) {
      // Clear existing custom colors and add the new one
      this.customColors = [color];
    }
  }

  private emitDataChange(): void {
    this.dataChange.emit({ ...this.data });
  }

  onSave(): void {
    this.save.emit({ ...this.data });
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
